import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { Document, Packer, Paragraph, TextRun, AlignmentType, convertInchesToTwip } from 'docx';

export interface MarginSettings {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

const DEFAULT_MARGINS: MarginSettings = {
  top: 0.2,
  bottom: 0.2,
  left: 0.2,
  right: 0.2,
};

/**
 * Export the resume content to PDF
 * Handles page break elements - content after page break goes to next page
 */
export async function exportToPDF(
  containerElement: HTMLElement,
  margins: MarginSettings = DEFAULT_MARGINS
): Promise<void> {
  try {
    // Find the actual content editable area (not the toolbar)
    const contentElement = containerElement.querySelector('.lexical-editor-input') as HTMLElement;
    if (!contentElement) {
      throw new Error('Could not find editor content element');
    }

    // Convert margins from inches to mm (1 inch = 25.4mm)
    const topMarginMm = margins.top * 25.4;
    const bottomMarginMm = margins.bottom * 25.4;
    const leftMarginMm = margins.left * 25.4;
    const rightMarginMm = margins.right * 25.4;

    // A4 dimensions: 210mm x 297mm
    const pageWidth = 210;
    const pageHeight = 297;
    
    // Calculate content area dimensions
    const contentAreaWidth = pageWidth - leftMarginMm - rightMarginMm;
    const contentAreaHeight = pageHeight - topMarginMm - bottomMarginMm;
    
    // Calculate content width in inches for rendering
    const contentWidthInches = (pageWidth - leftMarginMm - rightMarginMm) / 25.4;

    // Clone the content element for export
    const clonedElement = contentElement.cloneNode(true) as HTMLElement;
    clonedElement.style.position = 'absolute';
    clonedElement.style.left = '-9999px';
    clonedElement.style.width = `${contentWidthInches}in`;
    clonedElement.style.padding = '0';
    clonedElement.style.background = '#ffffff';
    clonedElement.style.color = '#000000';
    clonedElement.style.overflow = 'visible';
    clonedElement.style.height = 'auto';
    clonedElement.style.maxHeight = 'none';
    
    // Ensure black text color for all elements and hide page break visuals
    clonedElement.querySelectorAll('*').forEach((el) => {
      const htmlEl = el as HTMLElement;
      htmlEl.style.color = '#000000';
    });
    
    // Hide page break node visuals (we'll use them for positioning only)
    clonedElement.querySelectorAll('.page-break-node').forEach((el) => {
      const htmlEl = el as HTMLElement;
      htmlEl.style.visibility = 'hidden';
      htmlEl.style.height = '0';
      htmlEl.style.margin = '0';
      htmlEl.style.padding = '0';
      htmlEl.style.border = 'none';
    });
    
    document.body.appendChild(clonedElement);

    // Find page break positions before capturing canvas
    const pageBreakPositions: number[] = [];
    const pageBreakWrappers = clonedElement.querySelectorAll('.page-break-wrapper');
    pageBreakWrappers.forEach((wrapper) => {
      const wrapperEl = wrapper as HTMLElement;
      pageBreakPositions.push(wrapperEl.offsetTop);
    });

    const canvas = await html2canvas(clonedElement, {
      scale: 2,
      useCORS: true,
      backgroundColor: '#ffffff',
      logging: false,
      windowWidth: Math.round(contentWidthInches * 96),
    });

    document.body.removeChild(clonedElement);

    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });

    // Calculate scale factors
    const imgWidth = contentAreaWidth;
    const scale = imgWidth / canvas.width;
    const imgHeight = canvas.height * scale;
    
    // Convert page break positions from pixels to mm in the scaled image
    const pageBreaksMm = pageBreakPositions.map(pos => pos * scale);
    
    // Build list of page ranges (start position for each page)
    const pageStarts: number[] = [0];
    
    // Add page breaks from manual breaks
    pageBreaksMm.forEach(breakPos => {
      if (breakPos > 0 && breakPos < imgHeight) {
        pageStarts.push(breakPos);
      }
    });
    
    // Add automatic page breaks at content area boundaries
    let autoBreak = contentAreaHeight;
    while (autoBreak < imgHeight) {
      // Only add if there's no manual break nearby
      const hasNearbyManualBreak = pageStarts.some(
        start => Math.abs(start - autoBreak) < contentAreaHeight * 0.1
      );
      if (!hasNearbyManualBreak) {
        pageStarts.push(autoBreak);
      }
      autoBreak += contentAreaHeight;
    }
    
    // Sort and deduplicate
    const sortedPageStarts = Array.from(new Set(pageStarts)).sort((a, b) => a - b);
    
    // Render each page
    for (let i = 0; i < sortedPageStarts.length; i++) {
      if (i > 0) {
        pdf.addPage();
      }
      
      const startY = sortedPageStarts[i];
      const yOffset = topMarginMm - startY;
      
      pdf.addImage(
        imgData,
        'PNG',
        leftMarginMm,
        yOffset,
        imgWidth,
        imgHeight
      );

      // Add white rectangles to mask content outside margins
      pdf.setFillColor(255, 255, 255);
      pdf.rect(0, 0, pageWidth, topMarginMm, 'F');
      pdf.rect(0, pageHeight - bottomMarginMm, pageWidth, bottomMarginMm, 'F');

      // Safety check
      if (i > 50) {
        console.warn('PDF export: exceeded 50 pages, stopping');
        break;
      }
    }

    pdf.save('resume.pdf');
  } catch (error) {
    console.error('Error exporting to PDF:', error);
    throw error;
  }
}

/**
 * Get alignment from Lexical format
 */
function getAlignment(format: string | number | undefined): typeof AlignmentType[keyof typeof AlignmentType] {
  if (format === 'center' || format === 2) return AlignmentType.CENTER;
  if (format === 'right' || format === 3) return AlignmentType.RIGHT;
  if (format === 'justify' || format === 4) return AlignmentType.JUSTIFIED;
  return AlignmentType.LEFT;
}

/**
 * Parse font size from style string (e.g., "font-size: 24pt")
 */
function parseFontSize(style: string | undefined): number | undefined {
  if (!style) return undefined;
  const match = style.match(/font-size:\s*(\d+)pt/);
  if (match) {
    return parseInt(match[1], 10) * 2; // Convert pt to half-points for docx
  }
  return undefined;
}

/**
 * Parse line height from style string (e.g., "line-height: 1.5")
 * Returns value in twips (240 twips = 1 line at 12pt)
 */
function parseLineHeight(style: string | undefined): number | undefined {
  if (!style) return undefined;
  const match = style.match(/line-height:\s*([\d.]+)/);
  if (match) {
    const lineHeight = parseFloat(match[1]);
    // 240 twips = 12pt line, multiply by line height factor
    return Math.round(240 * lineHeight);
  }
  return undefined;
}

/**
 * Get line height from any of the children's styles
 */
function getLineHeightFromChildren(children: any[]): number | undefined {
  if (!Array.isArray(children)) return undefined;
  for (const child of children) {
    if (child && child.style) {
      const lineHeight = parseLineHeight(child.style);
      if (lineHeight) return lineHeight;
    }
  }
  return undefined;
}

/**
 * Parse text nodes and create TextRuns
 */
function parseTextChildren(children: any[], parentStyle?: string): TextRun[] {
  const textRuns: TextRun[] = [];

  for (const child of children) {
    if (child.type === 'text') {
      // Build options object
      const isBold = child.format ? Boolean(child.format & 1) : false;
      const isItalic = child.format ? Boolean(child.format & 2) : false;
      const isUnderline = child.format ? Boolean(child.format & 4) : false;
      const isStrike = child.format ? Boolean(child.format & 8) : false;

      // Handle inline style (font size, color)
      const style = child.style || parentStyle;
      const fontSize = style ? parseFontSize(style) : undefined;

      textRuns.push(new TextRun({
        text: child.text || '',
        bold: isBold || undefined,
        italics: isItalic || undefined,
        underline: isUnderline ? {} : undefined,
        strike: isStrike || undefined,
        size: fontSize,
      }));
    } else if (child.type === 'linebreak') {
      textRuns.push(new TextRun({ text: '', break: 1 }));
    }
  }

  return textRuns;
}

/**
 * Parse text nodes for headings (always bold, with specific size)
 */
function parseHeadingChildren(children: any[], fontSize: number): TextRun[] {
  const textRuns: TextRun[] = [];

  for (const child of children) {
    if (child.type === 'text') {
      const isItalic = child.format ? Boolean(child.format & 2) : false;
      const isUnderline = child.format ? Boolean(child.format & 4) : false;

      textRuns.push(new TextRun({
        text: child.text || '',
        bold: true,
        italics: isItalic || undefined,
        underline: isUnderline ? {} : undefined,
        size: fontSize,
      }));
    } else if (child.type === 'linebreak') {
      textRuns.push(new TextRun({ text: '', break: 1 }));
    }
  }

  return textRuns;
}

/**
 * Parse Lexical JSON content and convert to DOCX paragraphs
 */
function parseContentToDocx(contentJson: string): Paragraph[] {
  const paragraphs: Paragraph[] = [];

  if (!contentJson || contentJson.trim() === '') {
    console.error('Empty content JSON');
    paragraphs.push(
      new Paragraph({
        children: [new TextRun('No content to export')],
      })
    );
    return paragraphs;
  }

  try {
    const content = JSON.parse(contentJson);
    const root = content.root;

    if (root && root.children && Array.isArray(root.children)) {
      parseNodes(root.children, paragraphs);
    } else {
      console.error('Invalid content structure - missing root.children');
      paragraphs.push(
        new Paragraph({
          children: [new TextRun('Invalid content structure')],
        })
      );
    }
  } catch (error) {
    console.error('Error parsing content JSON:', error);
    console.error('Content JSON:', contentJson.substring(0, 500));
    paragraphs.push(
      new Paragraph({
        children: [new TextRun('Error parsing resume content - check console for details')],
      })
    );
  }

  return paragraphs;
}

/**
 * Safely parse a single node
 */
function parseNode(node: any, paragraphs: Paragraph[]): void {
  if (!node || !node.type) return;

  try {
    if (node.type === 'list') {
      // Parse list items inside the list
      if (node.children && Array.isArray(node.children)) {
        for (const listItem of node.children) {
          if (listItem && listItem.type === 'listitem') {
            const textRuns = parseTextChildren(listItem.children || []);
            paragraphs.push(new Paragraph({
              children: textRuns.length > 0 ? textRuns : [new TextRun('')],
              bullet: { level: listItem.indent || 0 },
              alignment: getAlignment(listItem.format),
            }));
          }
        }
      }
    } else if (node.type === 'heading') {
      const alignment = getAlignment(node.format);
      
      // Use bold styling instead of heading styles to avoid default blue color
      const isH1 = node.tag === 'h1';
      const fontSize = isH1 ? 28 : 22; // H1 = 14pt, H2 = 11pt (in half-points)
      
      // Create text runs with heading formatting
      const headingTextRuns = parseHeadingChildren(node.children || [], fontSize);

      paragraphs.push(new Paragraph({
        children: headingTextRuns.length > 0 ? headingTextRuns : [new TextRun({ text: '', bold: true, size: fontSize })],
        alignment,
        spacing: { before: isH1 ? 0 : 200, after: 100 },
      }));
    } else if (node.type === 'paragraph') {
      const textRuns = parseTextChildren(node.children || []);
      // Extract line height from first text child if present
      const lineHeight = getLineHeightFromChildren(node.children || []);
      const spacing: { line?: number } = {};
      if (lineHeight) {
        spacing.line = lineHeight;
      }
      paragraphs.push(new Paragraph({
        children: textRuns.length > 0 ? textRuns : [new TextRun('')],
        alignment: getAlignment(node.format),
        spacing: Object.keys(spacing).length > 0 ? spacing : undefined,
      }));
    } else if (node.type === 'listitem') {
      // Handle orphan list items
      const textRuns = parseTextChildren(node.children || []);
      const lineHeight = getLineHeightFromChildren(node.children || []);
      const spacing: { line?: number } = {};
      if (lineHeight) {
        spacing.line = lineHeight;
      }
      paragraphs.push(new Paragraph({
        children: textRuns.length > 0 ? textRuns : [new TextRun('')],
        bullet: { level: node.indent || 0 },
        alignment: getAlignment(node.format),
        spacing: Object.keys(spacing).length > 0 ? spacing : undefined,
      }));
    }
    // Silently ignore unknown node types
  } catch (nodeError) {
    console.error('Error parsing node:', node.type, nodeError);
  }
}

/**
 * Parse nodes array
 */
function parseNodes(nodes: any[], paragraphs: Paragraph[]): void {
  if (!Array.isArray(nodes)) return;
  
  for (const node of nodes) {
    parseNode(node, paragraphs);
  }
}

/**
 * Export the resume content to DOCX
 */
export async function exportToDOCX(
  contentJson: string,
  margins: MarginSettings = DEFAULT_MARGINS
): Promise<void> {
  try {
    const paragraphs = parseContentToDocx(contentJson);

    const doc = new Document({
      sections: [
        {
          properties: {
            page: {
              margin: {
                top: convertInchesToTwip(margins.top),
                bottom: convertInchesToTwip(margins.bottom),
                left: convertInchesToTwip(margins.left),
                right: convertInchesToTwip(margins.right),
              },
            },
          },
          children: paragraphs,
        },
      ],
    });

    const blob = await Packer.toBlob(doc);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'resume.docx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error exporting to DOCX:', error);
    throw error;
  }
}
